<template>
  <modal class="login-modal" @close-modal="close">
    <div class="login-modal__body">
      <header class="brand-band">
        <div class="brand-band__glow" />
        <div class="brand-band__content">
          <img
            src="@/assets/logo.png"
            alt="4casters"
            class="brand-band__logo"
          />
          <span class="brand-band__kicker">Trade sports.</span>
          <h1 class="brand-band__headline">Log in to keep going</h1>
          <p class="brand-band__subtitle">{{ subtitle }}</p>
          <div class="brand-band__props">
            <span class="brand-band__prop">
              <font-awesome-icon
                :icon="faChartLine"
                class="brand-band__prop-icon"
              />
              Peer-to-peer exchange
            </span>
            <span class="brand-band__prop">
              <font-awesome-icon :icon="faBolt" class="brand-band__prop-icon" />
              No commission on offers
            </span>
          </div>
        </div>
      </header>

      <form @submit.prevent="logIn">
        <div class="form-group form-label-group-floating">
          <input
            id="loginModalUsername"
            ref="usernameInput"
            v-model="username"
            type="text"
            name="username"
            placeholder="Username or email"
            class="form-control"
            autocomplete="username"
          />
          <label class="font-weight-normal" for="loginModalUsername">
            Username or email
          </label>
        </div>

        <div class="form-group form-label-group-floating">
          <input
            id="loginModalPassword"
            v-model="password"
            type="password"
            name="password"
            placeholder="Password"
            class="form-control"
            autocomplete="current-password"
          />
          <label class="font-weight-normal" for="loginModalPassword">
            Password
          </label>
        </div>

        <div v-show="challengeRequired" class="form-group text-center">
          <p class="login-modal__challenge-copy mb-2">
            Please confirm you're human to continue.
          </p>
          <div ref="challengeWidgetEl" class="challenge-widget" />
        </div>

        <div class="actions">
          <button class="login-btn" type="submit" :disabled="loading">
            <b>{{ loading ? 'Logging in…' : 'Log in' }}</b>
            <br />
            <span class="login-btn__caption">to your account</span>
          </button>
          <button
            class="sign-up-btn"
            type="button"
            :disabled="loading"
            @click="goToSignUp"
          >
            <b>Create an Account</b>
            <br />
            <span class="login-btn__caption">New to 4casters</span>
          </button>
        </div>

        <button
          class="forgot-password-link"
          type="button"
          :disabled="loading"
          @click="openPasswordReset"
        >
          Forgot password?
        </button>
      </form>
    </div>
  </modal>
  <reset-password-modal
    v-if="initiatePasswordResetModalShown"
    class="login-modal__reset"
    @close-modal="initiatePasswordResetModalShown = false"
    @send-email-success="sendEmailSuccess"
    @send-email-fail="sendEmailFail"
  />
</template>

<script setup>
/**
 * Login without leaving the page.
 *
 * A logged-out user who clicks a bet is already where they want to be — the
 * take-bet modal is open on the price they picked. Sending them to /log-in
 * threw that away. This popup logs them in on the spot and closes, leaving the
 * betting flow underneath intact and now authenticated.
 *
 * It is styled off the take-bet modal it opens on top of (same heading tokens,
 * same pill button pair) so the two read as one surface in both themes.
 */
import { nextTick, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useLoginForm } from '@/composables/useLoginForm';
import { emitter } from '@/core/emitter';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBolt } from '@fortawesome/free-solid-svg-icons/faBolt';
import { faChartLine } from '@fortawesome/free-solid-svg-icons/faChartLine';

// components
import Modal from '@/components/global/Modal.vue';
import ResetPasswordModal from '@/components/login/ResetPasswordModal.vue';

defineProps({
  // Why the popup opened, in the user's terms. Whatever they were doing is
  // still on screen underneath, so the default says exactly that.
  subtitle: {
    type: String,
    default: 'Pick up right where you left off.',
  },
});

const emits = defineEmits(['closeModal', 'logged-in']);
const router = useRouter();

const usernameInput = ref(null);

const {
  loading,
  username,
  password,
  challengeRequired,
  challengeWidgetEl,
  logIn,
} = useLoginForm({
  onSuccess: () => {
    emits('logged-in');
    close();
  },
});

onMounted(async () => {
  await nextTick();
  usernameInput.value?.focus();
});

function close() {
  emits('closeModal');
}

// Signing up is a multi-step flow of its own, so this one still navigates —
// but only when the user explicitly asks for it, and never mid-login: leaving
// while the request is in flight would unmount this popup and let the response
// resolve into setLoggedIn, dropping the user onto the signup form already
// logged in. The reset modal is gated for the same reason — a login landing
// underneath it would close the popup, and the reset UI with it.
function goToSignUp() {
  if (loading.value) return;
  close();
  router.push({
    name: 'SignUpPage',
    query: { redirect: router.currentRoute.value.fullPath },
  });
}

const initiatePasswordResetModalShown = ref(false);
function openPasswordReset() {
  if (loading.value) return;
  initiatePasswordResetModalShown.value = true;
}
function sendEmailFail(message) {
  initiatePasswordResetModalShown.value = false;
  if (message === 'AccountClosed') {
    emitter.emit('showAlert', {
      shown: true,
      type: 'email-fail',
      status: 'danger',
      title: 'Error! This account is closed',
    });
    return;
  }
  emitter.emit('showAlert', {
    shown: true,
    type: 'email-fail',
    status: 'danger',
    title: 'Error! This Email Is Not Registered',
    detail: 'Please enter the email associated with this account',
  });
}

function sendEmailSuccess() {
  initiatePasswordResetModalShown.value = false;
  emitter.emit('showAlert', {
    shown: true,
    type: 'email-success',
    status: 'ok',
    title: 'Success!',
    detail: 'Please check your email for your password reset link.',
  });
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@use '@/scss/main.scss';

$border-radius-button: 35px;

// The popup can be opened from on top of the take-bet modal, which is itself a
// `modal` (z-index 1001) — it has to land above it, and above it regardless of
// which component's stylesheet the bundler happens to emit last.
.login-modal {
  z-index: 1100 !important;
}
.login-modal__reset {
  z-index: 1200 !important;
}
.login-modal__body {
  width: 100%;
}
// On phones the shared modal is a full-height page, which left this short form
// stranded above half a screen of empty card. A login popup is a quick
// interruption — it belongs at the thumb as a bottom sheet sized to its own
// content, with the bet you clicked still visible above it.
.login-modal {
  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    align-items: flex-end;
  }

  :deep(.modal) {
    @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
      width: 100%;
      height: auto;
      max-height: 85vh;
      margin-top: auto;
      border-radius: 20px 20px 0 0;
      box-shadow: 0 -8px 28px rgba(0, 0, 0, 0.35);
      animation: login-sheet-up 0.22s ease-out;
    }
  }

  // The close button sits on the gradient band, not on the card.
  :deep(.modal-close-btn) {
    color: rgba(255, 255, 255, 0.8);

    &:hover {
      color: main.$white;
    }
  }
}
@keyframes login-sheet-up {
  from {
    transform: translateY(6%);
    opacity: 0.6;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

// The signed-out signup banner's treatment, compressed into a header band:
// same gradient, same gold accent, same value props. Bleeds past the modal's
// own padding so it reads as part of the card rather than a box inside it.
.brand-band {
  position: relative;
  overflow: hidden;
  margin: -1.5rem -1.5rem 1.75rem;
  padding: 1.75rem 1.5rem 1.5rem;
  border-radius: 15px 15px 0 0;
  text-align: center;
  background: linear-gradient(
    135deg,
    #2a3270 0%,
    #4a3f8f 35%,
    #5b4ba8 65%,
    #3a4595 100%
  );

  @media screen and (min-width: map.get(main.$grid-breakpoints, md)) {
    margin: -3rem -3rem 2rem;
    padding: 2rem 3rem 1.75rem;
  }

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    border-radius: 20px 20px 0 0;
    padding: 1.1rem 1.25rem 1rem;
    margin-bottom: 1.25rem;
  }

  [theme='night'] & {
    background: linear-gradient(
      135deg,
      #14225a 0%,
      #1f3370 35%,
      #2c4595 65%,
      #1a2a6b 100%
    );
  }
}
.brand-band__glow {
  position: absolute;
  top: -60%;
  left: -20%;
  width: 60%;
  height: 220%;
  pointer-events: none;
  background: radial-gradient(
    ellipse at center,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 70%
  );
}
.brand-band__content {
  position: relative;
  z-index: 1;
}
.brand-band__logo {
  height: 46px;
  width: auto;
  margin-bottom: 0.5rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    height: 30px;
    margin-bottom: 0.25rem;
  }
}
.brand-band__kicker {
  display: block;
  color: #ffd86b;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  text-shadow: 0 2px 8px rgba(255, 216, 107, 0.35);
}
.brand-band__headline {
  margin: 0.15rem 0 0;
  color: main.$white;
  font-family: 'Roboto Slab', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.25);

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: 1.1rem;
  }
}
.brand-band__subtitle {
  margin: 0.3rem 0 0;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.85rem;
}
.brand-band__props {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem 1.15rem;
  margin-top: 0.9rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    flex-wrap: nowrap;
    gap: 0.75rem;
    margin-top: 0.6rem;
    font-size: 0.72rem;
  }
}
.brand-band__prop {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: rgba(255, 255, 255, 0.92);
  font-size: 0.8rem;
  font-weight: 500;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    font-size: inherit;
    white-space: nowrap;
  }
}
.brand-band__prop-icon {
  color: #ffd86b;
  font-size: 0.8rem;
  filter: drop-shadow(0 2px 4px rgba(255, 216, 107, 0.4));
}
.login-modal__challenge-copy {
  font-size: 0.85rem;
  color: main.$black-203;
}
.challenge-widget {
  display: flex;
  justify-content: center;
}

// The grid's gutter is zeroed on the field columns (a global
// .form-label-group-floating rule), so a bootstrap row here would sit 15px
// inside the fields on each edge. A flex row with its own gap keeps the pills
// on exactly the same left and right edges as the inputs above them.
.actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1.25rem;
  }
}

// The button pair mirrors the take-bet modal's primary/secondary pills.
.login-btn,
.sign-up-btn {
  flex: 1 1 0;
  width: 100%;
  font-size: 0.925rem;
  padding: 0.75rem 1rem;
  border-radius: $border-radius-button;
  line-height: 1.25;
}
.login-btn {
  background-color: main.$color-primary_07;
  color: main.$grey-1100_02;

  &:disabled {
    background-color: main.$grey-1800;
    color: main.$white;
  }
  &:focus {
    background: main.$blue-1101;
  }
}
.sign-up-btn {
  background-color: main.$grey-1100_01;
  color: main.$color-primary_01;

  &:focus {
    background: main.$blue-103_01;
  }
  &:disabled {
    background-color: main.$grey-1800;
    color: main.$white;
  }
}
.login-btn__caption {
  font-size: 0.75rem;
  opacity: 0.75;
}
.forgot-password-link {
  display: block;
  width: 100%;
  padding: 0;
  background: none;
  border: 0;
  margin: 1.25rem 0 0;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    margin-top: 0.9rem;
  }

  text-align: center;
  cursor: pointer;
  font-size: 0.85rem;
  color: main.$color-primary_01;
  opacity: 0.8;

  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
  &:disabled {
    // The global button rule paints a grey slab on :disabled — this one is a
    // text link, so it just dims.
    background: none;
    opacity: 0.4;
    cursor: default;
    text-decoration: none;
  }
}
.form-group {
  margin: 0.75rem 0;

  @media screen and (max-width: map.get(main.$grid-breakpoints, sm)) {
    margin: 0.5rem 0;
  }
}
.form-label-group-floating {
  font-weight: lighter;

  // Only bootstrap's GRID is imported app-wide, so `.form-control` carries no
  // width of its own — left alone these fields sit at the browser's intrinsic
  // ~196px, stranded against the left edge of a much wider card. Width is the
  // ONLY thing to set here: the field's height and the floating label's
  // position are a matched pair of global rules, and overriding either half
  // leaves the label sitting on top of the value once it should have floated.
  > input {
    width: 100%;
  }
}
// The shared floating-label input draws no border of its own in night mode
// (--white_03 goes transparent), which leaves the fields as invisible slots on
// the dark card.
[theme='night'] .form-label-group-floating > input {
  border-color: rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.04);
  color: main.$color-primary_01;
}
[theme='night'] .form-label-group-floating > label {
  color: main.$white_04;
}
</style>
